import React, { useEffect, useRef, useState } from 'react'
import { InterfaceRoutes } from '../../interfaces/interfaceRoutes'
import './archivo-patrimonial.css'
import { getArchivoPatrimonial } from '../../config/services/strapi/archivo-patrimonial'
import TextSkeleton from '../../components/Skeleton/TextSkeleton'
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton'

const buildStableKey = (value: string, fallback: string) => {
  const normalized = value
    .trim()
    .replaceAll(' ', '-')
    .replaceAll(/[^a-zA-Z0-9-_]/g, '')

  return normalized || fallback
}

const renderChildren = (children: any[]) => {
  if (!children?.length) return null

  return children.map((c: any, ci: number) => {
    const txt: string = c?.text || ''
    const keyBase = `rc-${ci}-${(txt || '').slice(0, 12).split(/\s+/).join('-')}`
    if (c?.bold) {
      return <strong key={`${keyBase}-b`}>{txt}</strong>
    }

    if (txt.includes('\n')) {
      const parts = txt.split('\n')
      return parts.map((line, li) => (
        <React.Fragment key={`${keyBase}-${li}`}>
          {line}
          {li < parts.length - 1 ? <br /> : null}
        </React.Fragment>
      ))
    }

    return <React.Fragment key={`${keyBase}-t`}>{txt}</React.Fragment>
  })
}

const renderRichBlocks = (blocks: any, keyPrefix = '', firstAsHeading = false) => {
  if (!blocks) return null

  if (Array.isArray(blocks)) {
    return blocks.map((block: any, index: number) => {
      const rawChildren = block?.children || []
      const children = rawChildren.filter((c: any) => (c?.text || '').trim() !== 'Drag')
      const hasText = children.some((c: any) => c?.text && c.text.trim().length > 0)
      if (!hasText) return null

      if (firstAsHeading && index === 0) {
        return (
          <h2 key={`${keyPrefix}-h2-${index}`} className="ap-hero-title">
            {children.map((c: any, i: number) => (
              <React.Fragment key={`${keyPrefix}-h-${i}-${(c?.text||'').slice(0,10).split(/\s+/).join('-')}`}>{c?.text || ''}</React.Fragment>
            ))}
          </h2>
        )
      }

      return (
        <p key={`${keyPrefix}-p-${index}`}>{renderChildren(children)}</p>
      )
    })
  }

  if (typeof blocks === 'string') {
    return <p>{blocks}</p>
  }

  return null
}

const renderDescripcionWithSeparator = (blocks: any) => {
  if (!blocks) return null

  const list = Array.isArray(blocks) ? blocks : [blocks]
  if (!list.length) return null

  const first = list[0]
  const rest = list.slice(1)
  const second = rest[0]
  const tail = rest.slice(1)

  return (
    <>
      {renderRichBlocks([first], 'ap-desc-first')}
      {rest.length ? <div className="ap-separator" aria-hidden="true" /> : null}
      {second ? <div className="ap-second-paragraph">{renderRichBlocks([second], 'ap-desc-second')}</div> : null}
      {renderRichBlocks(tail, 'ap-desc-rest')}
    </>
  )
}

const getMediaUrl = (media: any) => {
  const base = process.env.REACT_APP_STRAPI_ENDPOINT || 'http://localhost:1337'
  const raw = media?.url || media?.data?.attributes?.url || ''
  if (!raw) return ''
  if (raw.startsWith('http')) return raw
  return `${base}${raw}`
}

const extractTextFromBlocks = (blocks: any) => {
  if (!blocks) return ''
  if (typeof blocks === 'string') return blocks
  if (!Array.isArray(blocks)) return ''

  const parts: string[] = []
  blocks.forEach((b: any) => {
    const children = b?.children || []
    const text = children.map((c: any) => c?.text || '').join('')
    if (text.trim()) parts.push(text.trim())
  })

  return parts.join(' ')
}

const extractHeroRawItems = (attrs: any) => {
  const rawHeroItems = attrs?.heroItem || attrs?.hero || []

  if (Array.isArray(rawHeroItems)) {
    return rawHeroItems
  }

  if (rawHeroItems?.data) {
    return Array.isArray(rawHeroItems.data) ? rawHeroItems.data : [rawHeroItems.data]
  }

  return []
}

const extractHeroMedia = (item: any) => {
  return item?.imagen
    || item?.image
    || item?.media
    || item?.attributes?.imagen
    || item?.attributes?.image
    || item?.attributes?.media
    || item?.attributes
    || item
}

const extractHeroContent = (item: any) => {
  return item?.contenido
    || item?.attributes?.contenido
    || item?.attributes?.texto
    || item?.attributes?.descripcion
    || null
}

const mapHeroData = (attrs: any) => {
  const images: any[] = []
  const captions: string[] = []
  const items = extractHeroRawItems(attrs)

  items.forEach((item: any) => {
    const media = extractHeroMedia(item)
    const mediaUrl = getMediaUrl(media)
    if (mediaUrl) {
      images.push(mediaUrl)
    }

    const content = extractHeroContent(item)
    const caption = extractTextFromBlocks(content)
    captions.push(caption)
  })

  return { images, captions }
}

const getLeftDescriptionBlocks = (data: any) => {
  if (data?.descripcion) return data.descripcion
  if (data?.detalle?.left) return data.detalle.left
  return data?.left
}

const useArchivoPatrimonialData = () => {
  const [loading, setLoading] = useState(true)
  const [apData, setApData] = useState<any>(null)
  const [heroItems, setHeroItems] = useState<any[]>([])
  const [heroCaptions, setHeroCaptions] = useState<string[]>([])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getArchivoPatrimonial()
        const attrs = res?.attributes || res || {}
        const { images, captions } = mapHeroData(attrs)

        setHeroItems(images)
        setHeroCaptions(captions)
        setApData(attrs)
      } catch (err) {
        console.error('Error cargando Archivo Patrimonial:', err)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  return { loading, apData, heroItems, heroCaptions }
}

const ArchivoPatrimonial = (_props: InterfaceRoutes) => {
  const { loading, apData, heroItems, heroCaptions } = useArchivoPatrimonialData()
  const [activeIndex, setActiveIndex] = useState(0)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const slideRefs = useRef<Array<HTMLDivElement | null>>([])

  const slidesCount = Math.max(heroItems.length, heroCaptions.length)

  const scrollToSlide = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const track = trackRef.current
    const slide = slideRefs.current[index]

    if (!track || !slide) {
      return
    }

    track.scrollTo({ left: slide.offsetLeft, behavior })
  }

  const goToSlide = (nextIndex: number, behavior: ScrollBehavior = 'smooth') => {
    if (!slidesCount) return
    const normalized = (nextIndex + slidesCount) % slidesCount
    setActiveIndex(normalized)
    scrollToSlide(normalized, behavior)
  }

  useEffect(() => {
    if (slidesCount <= 1) return
    const t = setInterval(() => {
      goToSlide(activeIndex + 1)
    }, 5000)
    return () => clearInterval(t)
  }, [activeIndex, slidesCount])

  useEffect(() => {
    const track = trackRef.current

    if (!track) {
      return
    }

    const onScroll = () => {
      const nearestIndex = slideRefs.current.reduce((closestIndex, slide, index) => {
        if (!slide) return closestIndex

        const closestSlide = slideRefs.current[closestIndex]
        if (!closestSlide) return index

        const distance = Math.abs(slide.offsetLeft - track.scrollLeft)
        const closestDistance = Math.abs(closestSlide.offsetLeft - track.scrollLeft)

        return distance < closestDistance ? index : closestIndex
      }, 0)

      setActiveIndex(nearestIndex)
    }

    track.addEventListener('scroll', onScroll, { passive: true })
    return () => track.removeEventListener('scroll', onScroll)
  }, [])

  const prev = () => goToSlide(activeIndex - 1)
  const next = () => goToSlide(activeIndex + 1)
  const leftDescriptionBlocks = getLeftDescriptionBlocks(apData)
  const rightBlocks = apData?.detalle || apData?.right

  return (
    <div className="archivo-page">
      {loading && (
        <div id="hero-ap-wrapper">
          <ImageSkeleton className="home-hero-skeleton" />
        </div>
      )}

      {!loading && (
        <div id="hero-carousel-ap" aria-label="Carrusel Archivo Patrimonial">
          <div
            ref={trackRef}
            className="ap-hero-track"
            aria-label="Galeria desplazable de imagenes"
          >
            {Array.from({ length: Math.max(1, slidesCount) }).map((_, i) => {
              const src = heroItems[i]
              const caption = heroCaptions[i] || ''
              const slideKey = buildStableKey(`${src || ''}-${caption}`, `slide-${i}`)
              const captionElement = caption ? <div className="ap-hero-caption">{caption}</div> : null
              const mediaElement = src
                ? (
                  <div className="ap-hero-image">
                    <img src={src} alt={`Archivo patrimonial ${i + 1}`} />
                    {captionElement}
                  </div>
                )
                : (
                  <div className="ap-hero-placeholder">
                    {captionElement}
                  </div>
                )

              return (
                <div
                  key={slideKey}
                  className="ap-hero-slide"
                  ref={(node) => { slideRefs.current[i] = node }}
                >
                  {mediaElement}
                </div>
              )
            })}
          </div>

            {slidesCount > 1 && (
              <>
                <button className="ap-hero-prev" onClick={prev} aria-label="Anterior">‹</button>
                <button className="ap-hero-next" onClick={next} aria-label="Siguiente">›</button>
              </>
            )}

            {slidesCount > 1 ? (
              <div className="ap-carousel-dots" role="tablist" aria-label="Selector de imagen">
                {Array.from({ length: slidesCount }).map((_, i) => {
                  const dotBase = `${heroItems[i] || ''}-${heroCaptions[i] || ''}`
                  const dotKey = buildStableKey(dotBase, `dot-${i}`)
                  return (
                    <button
                      key={`ap-dot-${dotKey}`}
                      type="button"
                      role="tab"
                      className={`ap-dot ${i === activeIndex ? 'is-selected' : ''}`}
                      aria-label={`Ir a imagen ${i + 1}`}
                      aria-selected={i === activeIndex}
                      onClick={() => goToSlide(i)}
                    />
                  )
                })}
              </div>
            ) : null}
        </div>
      )}

      <div id="main-content">
        <div className="page-content ap-content">
          <div className="ap-columns">
            <div className="ap-left">
              {loading ? <TextSkeleton /> : <h1 className="ap-title">Archivo Patrimonial Gasco</h1>}
              <h2 className="ap-left-title">{apData?.tituloLeft || ''}</h2>
              <div className="ap-left-desc">
                {loading ? <TextSkeleton /> : renderDescripcionWithSeparator(leftDescriptionBlocks)}
              </div>
              {apData?.textoColor ? <p className="ap-left-color" dangerouslySetInnerHTML={{ __html: apData.textoColor }} /> : null}
            </div>

            <div className="ap-right">
              {loading ? <TextSkeleton /> : renderRichBlocks(rightBlocks, 'ap-right')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ArchivoPatrimonial
